import { useId } from "react";

// Small form building blocks shared by the device settings (DeviceConfigPanel, SensorSetupFields) so every
// field looks and behaves the same: label, then the control, then one short hint or an error in its place.

const inputBase =
  "w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 " +
  "focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-500";
const inputBorder = (error) => (error ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-primary");

/** A label, the control and one line of help. `children(id, describedBy)` renders the control. */
export function Field({ label, hint, error, warning, className = "", children }) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error || warning || hint;
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children(id, note ? noteId : undefined, Boolean(error))}
      {note && (
        <p
          id={noteId}
          className={`mt-1.5 text-xs ${error ? "text-rose-600" : warning ? "text-amber-700" : "text-slate-500"}`}
        >
          {note}
        </p>
      )}
    </div>
  );
}

export function TextInput({ id, describedBy, invalid, className = "", ...props }) {
  return (
    <input
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={`${inputBase} ${inputBorder(invalid)} ${className}`}
      {...props}
    />
  );
}

/** A number input with its unit shown inside the box, on the right. */
export function UnitInput({ id, describedBy, invalid, unit, className = "", ...props }) {
  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        type="number"
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className={`${inputBase} ${inputBorder(invalid)} pr-12 tabular-nums`}
        {...props}
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-400">
        {unit}
      </span>
    </div>
  );
}

export function SelectInput({ id, describedBy, invalid, children, className = "", ...props }) {
  return (
    <select
      id={id}
      aria-describedby={describedBy}
      className={`${inputBase} ${inputBorder(invalid)} ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

/** An on/off switch with its label and hint beside it. */
export function Toggle({ checked, onChange, label, hint }) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
          checked ? "bg-primary" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
      <div>
        <p id={`${id}-label`} className="text-sm font-medium text-slate-700">
          {label}
        </p>
        {hint && (
          <p id={`${id}-hint`} className="mt-0.5 text-xs text-slate-500">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * One settings card: a title and a sentence on the left (above on phones), the fields on the right.
 * `badge` says how the card's changes take effect when that differs from the page's Save.
 */
export function SettingsSection({ title, description, badge, icon: Icon, children, footer }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            {Icon && <Icon className="h-4 w-4 text-slate-400" aria-hidden="true" />}
            {title}
          </h3>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
          {badge && <div className="mt-3">{badge}</div>}
        </div>
        <div className="min-w-0 space-y-5">{children}</div>
      </div>
      {footer && (
        <div className="flex flex-wrap items-center justify-end gap-3 rounded-b-xl border-t border-slate-100 bg-slate-50 px-5 py-3 sm:px-6">
          {footer}
        </div>
      )}
    </section>
  );
}

const BADGE_TONES = {
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
};

export function Badge({ icon: Icon, tone = "slate", children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${BADGE_TONES[tone]}`}>
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </span>
  );
}

export const buttonPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-on-primary " +
  "shadow-sm transition-colors hover:bg-primary-container focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 " +
  "disabled:cursor-not-allowed disabled:opacity-50";
export const buttonSecondary =
  "inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold " +
  "text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 " +
  "disabled:cursor-not-allowed disabled:opacity-50";
